'use client'

import { useMemo } from 'react'
import { Globe, Mail } from 'lucide-react'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { EntryRow } from './entryTypes'
import type { EntryDomainLink } from './campaignTypes'
import { parseDbTimestamp } from '@/lib/dates'

// The campaign as the primary record rather than a badge hanging off one
// entry. A campaign's members are separate reports of the same thing —
// different reporters, different inboxes, sometimes days apart — so reading
// one member's message and a "3 more" count leaves the actual content of the
// other two invisible. These two blocks are what make the campaign readable
// on its own: what the members agree on (consolidated), and what each one
// actually said (timeline).
//
// Everything here is derived from the member rows already fetched for the
// campaign — no extra request, and nothing stored.

const REVISION_LABEL: Record<string, { label: string; tone: string }> = {
  unseen: { label: 'Pendente', tone: 'border-warning-border text-warning' },
  accept: { label: 'Aceite', tone: 'border-success/40 text-success' },
  'auto-accept': { label: 'Auto', tone: 'border-success/40 text-success' },
  'accept-hidden': { label: 'Aceite (oculto)', tone: 'border-success/40 text-success' },
  reject: { label: 'Rejeitado', tone: 'border-destructive/40 text-destructive' },
  defer1: { label: 'Adiado', tone: 'border-warning-border text-warning' },
}

function fmt(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d)
}

function flatOf(e: EntryRow): Record<string, any> {
  return (e.processed_entries_flat ?? {}) as Record<string, any>
}

function entryTimestamp(e: EntryRow): string | null {
  const flat = flatOf(e)
  return flat.created_at ?? (e.created_at as string | undefined) ?? null
}

/** Distinct, non-empty values in member order — used for every "N variants"
 *  style field below, where the point is precisely that members can disagree. */
function distinct(values: (string | null | undefined)[]): string[] {
  const out: string[] = []
  for (const v of values) {
    const s = typeof v === 'string' ? v.trim() : ''
    if (s && !out.includes(s)) out.push(s)
  }
  return out
}

export type CampaignConsolidated = ReturnType<typeof consolidateCampaign>

export function consolidateCampaign(entries: EntryRow[]) {
  const ordered = [...entries].sort((a, b) => {
    const at = parseDbTimestamp(entryTimestamp(a) ?? undefined)?.getTime() ?? 0
    const bt = parseDbTimestamp(entryTimestamp(b) ?? undefined)?.getTime() ?? 0
    return at - bt // oldest first: a campaign reads as a story forward in time
  })

  const times = ordered.map(entryTimestamp).filter((t): t is string => !!t)

  // One row per distinct URL actually reported, with how many members
  // reported it — a campaign's members often report different paths on the
  // same host, and which URL each one saw is the operational detail.
  const targetMap = new Map<string, { url: string; count: number; firstSeen: string | null; domainUrl: string | null }>()
  for (const e of ordered) {
    const links: EntryDomainLink[] = flatOf(e).entry_domains ?? []
    const ts = entryTimestamp(e)
    for (const link of links) {
      const url = link.original_url || link.domain_url
      if (!url) continue
      const existing = targetMap.get(url)
      if (existing) existing.count += 1
      else targetMap.set(url, { url, count: 1, firstSeen: ts, domainUrl: link.domain_url ?? null })
    }
  }

  return {
    ordered,
    reportCount: ordered.length,
    firstSeen: times.length ? times.reduce((a, b) => (a < b ? a : b)) : null,
    lastSeen: times.length ? times.reduce((a, b) => (a > b ? a : b)) : null,
    sources: distinct(ordered.map((e) => flatOf(e).source ?? (e as any).source)),
    reporters: distinct(ordered.map((e) => flatOf(e).sender ?? (e as any).sender)),
    messageTypes: distinct(ordered.map((e) => flatOf(e).message_type_name)),
    platforms: distinct(ordered.map((e) => flatOf(e).platform_name ?? flatOf(e).platform_namelong)),
    subjects: distinct(ordered.map((e) => flatOf(e).message_subject)),
    targets: [...targetMap.values()],
    anyScam: ordered.some((e) => flatOf(e).is_scam === true),
    revisionStatuses: distinct(ordered.map((e) => (e.revision_status as string | undefined) ?? flatOf(e).revision_status)),
  }
}

function Field({ label, badge, children, theme }: {
  label: string
  badge?: string
  children: React.ReactNode
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center gap-2">
        <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{label}</span>
        {badge && (
          <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest border-warning-border text-warning`}>
            {badge}
          </span>
        )}
      </div>
      <div className={`rounded border px-3 py-2 text-[13px] ${c.line} ${c.soft} ${c.text}`}>{children}</div>
    </div>
  )
}

export function ConsolidatedRecord({ data, theme }: { data: CampaignConsolidated; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const none = <span className={c.faint}>—</span>
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Field label="Message type" theme={theme} badge={data.messageTypes.length > 1 ? `${data.messageTypes.length} variants` : undefined}>
        {data.messageTypes.join(' · ') || none}
      </Field>
      <Field label="Platform / brand" theme={theme}>
        {data.platforms.join(' · ') || none}
      </Field>
      <Field label="Sources" theme={theme}>
        <span className="font-mono text-[12px]">{data.sources.join(' · ') || '—'}</span>
      </Field>
      <Field label="Reporters" theme={theme} badge={data.reporters.length > 1 ? `${data.reporters.length} senders` : undefined}>
        {data.reporters.join(', ') || none}
      </Field>
      <Field label="First / last seen" theme={theme}>
        <span className="font-mono text-[12px]">{fmt(data.firstSeen)} → {fmt(data.lastSeen)}</span>
      </Field>
      <Field label="Revision status" theme={theme} badge={data.revisionStatuses.length > 1 ? `${data.revisionStatuses.length} differ` : undefined}>
        <div className="flex flex-wrap gap-1.5">
          {data.revisionStatuses.length === 0 ? none : data.revisionStatuses.map((s) => (
            <span key={s} className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${REVISION_LABEL[s]?.tone ?? `${c.line} ${c.faint}`}`}>
              {REVISION_LABEL[s]?.label ?? s}
            </span>
          ))}
        </div>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Primary subject" theme={theme} badge={data.subjects.length > 1 ? `${data.subjects.length} variants` : undefined}>
          {data.subjects.length === 0 ? none : (
            <ul className="space-y-1">
              {data.subjects.map((s) => <li key={s}>{s}</li>)}
            </ul>
          )}
        </Field>
      </div>
    </div>
  )
}

// Every member's actual content, oldest first — the thing that was missing
// when a campaign was only reachable as a count next to one entry's message.
export function MergedTimeline({
  data,
  theme,
  onOpenEntry,
}: {
  data: CampaignConsolidated
  theme: ProcessedTheme
  onOpenEntry: (entry: EntryRow) => void
}) {
  const c = watchClasses(theme)
  return (
    <div className="space-y-3">
      {data.ordered.map((e) => {
        const flat = flatOf(e)
        const status = (e.revision_status as string | undefined) ?? flat.revision_status ?? 'unseen'
        const badge = REVISION_LABEL[status] ?? { label: status, tone: `${c.line} ${c.faint}` }
        const sender = flat.sender ?? (e as any).sender
        const subject = flat.message_subject
        const body = flat.message_content ?? (e as any).message_content
        const source = flat.source ?? (e as any).source
        return (
          <div key={e.id} className={`rounded border p-3 ${c.line} ${c.panel}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`font-mono text-[11px] ${c.text}`}>{String(e.id).slice(0, 8)}</span>
              <span className={`font-mono text-[11px] ${c.faint}`}>{fmt(entryTimestamp(e))}</span>
              <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${badge.tone}`}>
                {badge.label}
              </span>
              {source && (
                <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${c.line} ${c.faint}`}>
                  {source}
                </span>
              )}
              <button
                type="button"
                onClick={() => onOpenEntry(e)}
                className={`ml-auto rounded border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest transition-colors hover:text-signal hover:border-signal ${c.line} ${c.faint}`}
              >
                Raw / processed →
              </button>
            </div>
            {sender && (
              <div className={`mb-1 flex items-center gap-1.5 text-[12px] ${c.text}`}>
                <Mail className="h-3 w-3 shrink-0" />
                <span className="font-mono break-all">{sender}</span>
              </div>
            )}
            {subject && <div className={`mb-2 text-[13px] ${c.text}`}>{subject}</div>}
            {body ? (
              <pre className={`max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border px-3 py-2 text-[12px] leading-5 ${c.line} ${c.soft} ${c.muted}`}>
                {String(body)}
              </pre>
            ) : (
              <span className={`text-[12px] ${c.faint}`}>No message content</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Distinct URLs the members actually reported. Counts come from the members'
// own entry_domains, so "2 reports" means two members reported that exact
// URL — not that the campaign has two members.
// Live/down + probe evidence for one reported url, as returned by
// /campaigngroups/targets (getCampaignTargets).
export type CampaignTargetEvidence = {
  url: string
  domainId: string | null
  reportCount: number
  firstSeen: string | null
  lastCheckedAt: string | null
  manualInactive: boolean
  browserDataCount: number
  probeRunCount: number
  mobileIsAlive: boolean | null
  desktopIsAlive: boolean | null
  probeAnyAlive: boolean | null
  contentIsActive: boolean | null
  brandImpersonation: boolean | null
  probeNode: string | null
}

// Deliberately not collapsed into a single boolean: a target can render fine
// in a browser capture while the probe's content check says it is not active
// (and vice versa), and which of those is true matters operationally. LIVE is
// claimed when anything positive came back, DOWN only when the signals that
// did come back all say so, UNKNOWN when nothing has been checked. A manual
// mark always wins — that's a human overriding the probes on purpose.
//
// Colours follow the plain liveness convention — green reachable, red not —
// rather than a threat reading (where a live phishing host would be the
// alarming one). Keep the two consistent if either is ever revisited.
function targetStatus(ev: CampaignTargetEvidence | undefined): {
  label: string
  tone: string
  detail: string
} {
  if (!ev) return { label: 'not checked', tone: 'border-border text-muted-foreground', detail: 'No probe or browser evidence for this URL yet.' }
  if (ev.manualInactive) return { label: 'down · manual', tone: 'border-destructive/40 text-destructive', detail: 'Marked inactive by an operator, overriding the probes.' }

  const positives: string[] = []
  const negatives: string[] = []
  if (ev.mobileIsAlive === true) positives.push('mobile render'); else if (ev.mobileIsAlive === false) negatives.push('mobile render')
  if (ev.desktopIsAlive === true) positives.push('desktop render'); else if (ev.desktopIsAlive === false) negatives.push('desktop render')
  if (ev.contentIsActive === true) positives.push('content active'); else if (ev.contentIsActive === false) negatives.push('content active')
  if (ev.probeAnyAlive === true) positives.push('probe reachable'); else if (ev.probeAnyAlive === false) negatives.push('probe reachable')

  if (positives.length === 0 && negatives.length === 0) {
    return { label: 'not checked', tone: 'border-border text-muted-foreground', detail: 'No probe or browser evidence for this URL yet.' }
  }
  const detail = [
    positives.length ? `alive: ${positives.join(', ')}` : null,
    negatives.length ? `not alive: ${negatives.join(', ')}` : null,
    ev.probeNode ? `probed from ${ev.probeNode}` : null,
  ].filter(Boolean).join(' · ')

  if (positives.length > 0) {
    return {
      label: negatives.length > 0 ? 'live · mixed' : 'live',
      // Amber when the signals disagree — still reachable, but "renders yet
      // content check fails" is worth distinguishing from a clean live.
      tone: negatives.length > 0
        ? 'border-warning-border text-warning'
        : 'border-success/40 text-success',
      detail,
    }
  }
  return { label: 'down', tone: 'border-destructive/40 text-destructive', detail }
}

export function CampaignTargets({
  data,
  theme,
  evidence,
  evidenceLoading,
  onSelectDomain,
}: {
  data: CampaignConsolidated
  theme: ProcessedTheme
  /** Keyed by url — absent until /campaigngroups/targets resolves. */
  evidence: Map<string, CampaignTargetEvidence>
  evidenceLoading: boolean
  onSelectDomain?: (domainUrl: string) => void
}) {
  const c = watchClasses(theme)
  const targets = useMemo(
    () => [...data.targets].sort((a, b) => b.count - a.count || a.url.localeCompare(b.url)),
    [data.targets],
  )
  if (targets.length === 0) {
    return <p className={`text-[12px] ${c.faint}`}>No reported URLs on this campaign.</p>
  }
  return (
    <div className="space-y-2">
      {targets.map((t) => {
        const ev = evidence.get(t.url)
        const status = targetStatus(ev)
        return (
          <button
            key={t.url}
            type="button"
            onClick={() => onSelectDomain?.(t.domainUrl ?? t.url)}
            className={`block w-full rounded border p-2 text-left transition-colors hover:border-signal ${c.line} ${c.soft}`}
          >
            <div className="flex items-start gap-1.5">
              <Globe className={`mt-0.5 h-3 w-3 shrink-0 ${c.faint}`} />
              <span className="font-mono text-[11px] break-all text-signal">{t.url}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-[18px]">
              <span
                title={status.detail}
                className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${
                  evidenceLoading && !ev ? `${c.line} ${c.faint} animate-pulse` : status.tone
                }`}
              >
                {evidenceLoading && !ev ? 'checking…' : status.label}
              </span>
              {ev?.brandImpersonation === true && (
                <span
                  title="The probe's content-layer brand_impersonation test came back positive for this URL."
                  className="rounded border border-warning-border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-warning"
                >
                  impersonation
                </span>
              )}
              <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
                {t.count} {t.count === 1 ? 'report' : 'reports'}
              </span>
            </div>
            <div className={`mt-1 pl-[18px] font-mono text-[10px] ${c.faint}`}>
              {fmt(t.firstSeen)}
              {ev && (ev.probeRunCount > 0 || ev.browserDataCount > 0) && (
                <> · {ev.probeRunCount} probe{ev.probeRunCount === 1 ? '' : 's'} · {ev.browserDataCount} capture{ev.browserDataCount === 1 ? '' : 's'}</>
              )}
              {ev?.lastCheckedAt && <> · last {fmt(ev.lastCheckedAt)}</>}
            </div>
          </button>
        )
      })}
    </div>
  )
}
